import { useState, useEffect } from 'react';
import type { Account, AccountType, AccountCurrency } from '../../../hooks/useAccounts';
import { BankSearch } from './BankSearch';
import { CRYPTO_ASSETS } from '../../../mocks/cryptoAssets';
import { CurrencyInput } from '../../../components/base/CurrencyInput';
import { trackEvent } from '../../../lib/analytics';

interface AccountModalProps {
  open: boolean;
  account?: Account | null;
  onClose: () => void;
  onSave: (values: Omit<Account, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'is_archived'>) => Promise<{ error: string | null }>;
}

type FieldGroup = 'banking' | 'investment' | 'wallet' | 'foreign' | 'crypto';

const TYPE_TO_GROUP: Record<AccountType, FieldGroup> = {
  corrente: 'banking', digital: 'banking', poupanca: 'banking', pj: 'banking',
  investimento: 'investment', corretora: 'investment',
  carteira: 'wallet', dinheiro: 'wallet',
  dolar: 'foreign', euro: 'foreign', outra_moeda: 'foreign', internacional: 'foreign',
  cripto: 'crypto',
};

const CURRENCIES: { value: AccountCurrency; label: string; symbol: string }[] = [
  { value: 'BRL', label: 'Real Brasileiro', symbol: 'R$' },
  { value: 'USD', label: 'Dólar Americano', symbol: '$' },
  { value: 'EUR', label: 'Euro', symbol: '€' },
  { value: 'GBP', label: 'Libra Esterlina', symbol: '£' },
  { value: 'ARS', label: 'Peso Argentino', symbol: '$' },
  { value: 'BTC', label: 'Bitcoin', symbol: '₿' },
  { value: 'ETH', label: 'Ethereum', symbol: 'Ξ' },
  { value: 'USDT', label: 'Tether (USDT)', symbol: '₮' },
];

const TYPES: { value: AccountType; label: string; icon: string; group: FieldGroup }[] = [
  { value: 'corrente', label: 'Conta Corrente', icon: 'ri-bank-line', group: 'banking' },
  { value: 'digital', label: 'Conta Digital', icon: 'ri-smartphone-line', group: 'banking' },
  { value: 'poupanca', label: 'Poupança', icon: 'ri-safe-line', group: 'banking' },
  { value: 'pj', label: 'Conta PJ', icon: 'ri-building-line', group: 'banking' },
  { value: 'carteira', label: 'Carteira', icon: 'ri-wallet-3-line', group: 'wallet' },
  { value: 'dinheiro', label: 'Dinheiro', icon: 'ri-money-dollar-circle-line', group: 'wallet' },
  { value: 'investimento', label: 'Investimentos', icon: 'ri-stock-line', group: 'investment' },
  { value: 'corretora', label: 'Corretora', icon: 'ri-line-chart-line', group: 'investment' },
  { value: 'dolar', label: 'Conta em Dólar', icon: 'ri-currency-line', group: 'foreign' },
  { value: 'euro', label: 'Conta em Euro', icon: 'ri-currency-line', group: 'foreign' },
  { value: 'outra_moeda', label: 'Outra Moeda', icon: 'ri-exchange-line', group: 'foreign' },
  { value: 'internacional', label: 'Internacional', icon: 'ri-global-line', group: 'foreign' },
  { value: 'cripto', label: 'Criptoativos', icon: 'ri-bit-coin-line', group: 'crypto' },
];

const COLORS = ['#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#64748b','#f97316','#84cc16','#14b8a6','#a855f7','#6366f1'];

const DEFAULT_CURRENCY_BY_TYPE: Record<AccountType, AccountCurrency> = {
  corrente: 'BRL', digital: 'BRL', poupanca: 'BRL', pj: 'BRL',
  carteira: 'BRL', dinheiro: 'BRL',
  investimento: 'BRL', corretora: 'BRL',
  dolar: 'USD', euro: 'EUR', outra_moeda: 'USD', internacional: 'USD',
  cripto: 'BTC',
};

const defaultForm = (): Omit<Account, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'is_archived'> => ({
  name: '', nickname: '', type: 'corrente', bank: '', bank_name: '', bank_code: '',
  branch_number: '', account_number: '', account_digit: '', currency: 'BRL',
  initial_balance: 0, current_balance: 0, color: '#10b981', is_active: true, notes: '',
});

export const AccountModal = ({ open, account, onClose, onSave }: AccountModalProps) => {
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (open) {
      if (account) {
        setForm({
          name: account.name ?? '', nickname: account.nickname ?? '',
          type: account.type ?? 'corrente', bank: account.bank ?? '',
          bank_name: account.bank_name ?? '', bank_code: account.bank_code ?? '',
          branch_number: account.branch_number ?? '', account_number: account.account_number ?? '',
          account_digit: account.account_digit ?? '', currency: account.currency ?? 'BRL',
          initial_balance: account.initial_balance ?? 0, current_balance: account.current_balance ?? 0,
          color: account.color ?? '#10b981', is_active: account.is_active ?? true,
          notes: account.notes ?? '',
        });
      } else {
        setForm(defaultForm());
      }
      setErrorMsg('');
    }
  }, [open, account]);

  const group = TYPE_TO_GROUP[form.type];
  const showBankSearch = group === 'banking' || group === 'investment' || group === 'foreign';
  const showBranchAccount = group === 'banking';
  const showCurrency = group === 'foreign' || group === 'crypto';
  const showCryptoAsset = group === 'crypto';

  const handleTypeChange = (type: AccountType) => {
    setForm(p => ({
      ...p, type,
      currency: DEFAULT_CURRENCY_BY_TYPE[type],
      bank: '', bank_name: '', bank_code: '',
      branch_number: '', account_number: '', account_digit: '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSaving(true);
    const payload = { ...form, bank_name: form.bank };
    const result = await onSave(payload);
    setSaving(false);
    if (result?.error) setErrorMsg('Erro ao salvar. Tente novamente.');
    else {
      if (!account) trackEvent('account_created', { type: form.type });
      onClose();
    }
  };

  if (!open) return null;

  const bankLabel = group === 'investment' || group === 'corretora'
    ? 'Corretora / Instituição'
    : group === 'foreign' || group === 'internacional'
    ? 'Banco Internacional'
    : 'Banco / Instituição';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-lg w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-base font-700 text-gray-900">{account ? 'Editar Conta' : 'Nova Conta'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 overflow-y-auto flex-1">

          {/* Tipo */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-2 block">Tipo de conta</label>
            <div className="grid grid-cols-4 gap-2">
              {TYPES.map(t => (
                <button key={t.value} type="button" onClick={() => handleTypeChange(t.value)}
                  className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-600 transition-all cursor-pointer ${
                    form.type === t.value ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <i className={`${t.icon} text-sm`} />
                  <span className="text-center leading-tight">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Nome + Apelido */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Nome da conta *</label>
              <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Ex: Itaú Principal"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
            </div>
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
                Apelido <span className="text-gray-400 normal-case font-400">(opcional)</span>
              </label>
              <input value={form.nickname} onChange={e => setForm(p => ({ ...p, nickname: e.target.value }))}
                placeholder="Ex: Conta do dia a dia"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
            </div>
          </div>

          {/* Banco (condicional — não cripto) */}
          {showBankSearch && (
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">{bankLabel}</label>
              <BankSearch
                value={form.bank}
                bankCode={form.bank_code}
                onChange={(name, code) => setForm(p => ({ ...p, bank: name, bank_name: name, bank_code: code }))}
                placeholder="Buscar banco ou código..."
              />
            </div>
          )}

          {/* Ativo cripto — autocomplete com lista de criptos */}
          {showCryptoAsset && (
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
                Ativo / Moeda
              </label>
              <BankSearch
                value={form.bank}
                bankCode={form.bank_code}
                items={CRYPTO_ASSETS}
                icon="ri-bit-coin-line"
                codeLabel="Ticker"
                placeholder="Buscar cripto: Bitcoin, ETH, SOL..."
                emptyMessage="Criptoativo não encontrado"
                onChange={(name, code) => setForm(p => ({ ...p, bank: name, bank_name: name, bank_code: code }))}
              />
            </div>
          )}

          {/* Agência + Conta (só banking) */}
          {showBranchAccount && (
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-4">
                <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Agência</label>
                <input value={form.branch_number} onChange={e => setForm(p => ({ ...p, branch_number: e.target.value }))}
                  placeholder="0001"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
                />
              </div>
              <div className="col-span-5">
                <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Nº da conta</label>
                <input value={form.account_number} onChange={e => setForm(p => ({ ...p, account_number: e.target.value }))}
                  placeholder="000000"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
                />
              </div>
              <div className="col-span-3">
                <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Dígito</label>
                <input value={form.account_digit} onChange={e => setForm(p => ({ ...p, account_digit: e.target.value }))}
                  placeholder="0" maxLength={2}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
                />
              </div>
            </div>
          )}

          {/* Moeda (condicional) */}
          {showCurrency && (
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Moeda da conta</label>
              <div className="grid grid-cols-4 gap-2">
                {CURRENCIES.filter(c => {
                  if (group === 'crypto') return ['BTC', 'ETH', 'USDT', 'USD'].includes(c.value);
                  return ['BRL', 'USD', 'EUR', 'GBP', 'ARS'].includes(c.value);
                }).map(c => (
                  <button key={c.value} type="button"
                    onClick={() => setForm(p => ({ ...p, currency: c.value }))}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-xl border text-xs font-600 transition-all cursor-pointer ${
                      form.currency === c.value ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-sm font-700">{c.symbol}</span>
                    {c.value}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Saldo será exibido na moeda selecionada. Conversão automática em breve.
              </p>
            </div>
          )}

          {/* Saldo inicial */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Saldo inicial</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-500 z-10 pointer-events-none">
                {CURRENCIES.find(c => c.value === form.currency)?.symbol ?? 'R$'}
              </span>
              <CurrencyInput
                value={form.initial_balance}
                onChange={(val) => {
                  setForm(p => ({ ...p, initial_balance: val, current_balance: account ? p.current_balance : val }));
                }}
                placeholder="0,00"
                maxDecimals={group === 'crypto' ? 8 : 2}
                className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              O saldo atual é calculado automaticamente com base nos lançamentos vinculados
            </p>
          </div>

          {/* Cor */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Cor</label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLORS.map(c => (
                <button key={c} type="button" onClick={() => setForm(p => ({ ...p, color: c }))}
                  style={{ backgroundColor: c }}
                  className={`w-7 h-7 rounded-full cursor-pointer transition-transform ${form.color === c ? 'scale-125 ring-2 ring-offset-2 ring-gray-400' : ''}`}
                />
              ))}
            </div>
          </div>

          {/* Observação */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
              Observação <span className="normal-case font-400 text-gray-400">(opcional)</span>
            </label>
            <textarea rows={2} maxLength={300} value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Anotações pessoais sobre esta conta..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 resize-none"
            />
          </div>

          {errorMsg && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
              <i className="ri-error-warning-line" />
              {errorMsg}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap">
              {saving ? 'Salvando...' : account ? 'Salvar alterações' : 'Criar conta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
